import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { Form, Formik, FormikProps } from 'formik';
import React from 'react';

import { finleyAssertUnreachable } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { AssetVerificationConfig } from './postgresQueryConfig/AssetVerificationConfig';
import { OrgProvidedInputConfig } from './postgresQueryConfig/OrgProvidedInputConfig';
import { PrincipalBalanceDailyAverageConfig } from './postgresQueryConfig/PrincipalBalanceDailyAverageConfig';
import { PrincipalBalancePointInTimeConfig } from './postgresQueryConfig/PrincipalBalancePointInTimeConfig';
import { TAccountsSumConfig } from './postgresQueryConfig/TAccountsSumConfig';
import { TextInputFormField } from 'packages/common-frontend/components/FormField';
import { WipConfig } from 'packages/creditAgreements/ui/types';

type PostgresQueryType = CreditAgreement.Config.CommonPostgresQuery.Query.QueryAllResultSet;

type PostgresQueryTypeViewProps<T extends PostgresQueryType> = {
  formProps: FormikProps<T>;
  wipConfig: WipConfig;
};
const PostgresQueryTypeView = <T extends PostgresQueryType>({
  formProps,
  wipConfig,
}: PostgresQueryTypeViewProps<T>) => {
  const { type } = formProps.values;

  // TS won't narrow the generic FormikProps, so casting as pushing down into concrete component
  switch (type) {
    case 'assetVerification':
      return (
        <AssetVerificationConfig
          formProps={
            (formProps as unknown) as FormikProps<CreditAgreement.Config.CommonPostgresQuery.Query.AssetVerificationQuery>
          }
        />
      );
    case 'principalBalanceDailyAverage':
      return (
        <PrincipalBalanceDailyAverageConfig
          formProps={
            (formProps as unknown) as FormikProps<CreditAgreement.Config.CommonPostgresQuery.Query.PrincipalBalanceDailyAverageQuery>
          }
        />
      );
    case 'principalBalancePointInTime':
      return (
        <PrincipalBalancePointInTimeConfig
          formProps={
            (formProps as unknown) as FormikProps<CreditAgreement.Config.CommonPostgresQuery.Query.PrincipalBalancePointInTimeQuery>
          }
        />
      );
    case 'tAccountsSum':
      return (
        <TAccountsSumConfig
          formProps={
            (formProps as unknown) as FormikProps<CreditAgreement.Config.CommonPostgresQuery.Query.TAccountsSumQuery>
          }
          tAccounts={wipConfig.tAccounts}
          organizationMap={wipConfig.organizationMap}
        />
      );

    case 'orgProvidedInput':
      return (
        <OrgProvidedInputConfig
          formProps={
            (formProps as unknown) as FormikProps<CreditAgreement.Config.CommonPostgresQuery.Query.OrgProvidedInputQuery>
          }
          keys={wipConfig.orgProvidedInputKeys}
        />
      );
    default:
      return finleyAssertUnreachable(type);
  }
};

type EditPostgresQueryModalProps<T extends PostgresQueryType> = {
  isOpen: boolean;
  onCancel: Function;
  onSave: Function;
  postgresQuery: T;
  wipConfig: WipConfig;
};
export const EditPostgresQueryModal = <T extends PostgresQueryType>({
  isOpen,
  onCancel,
  onSave,
  postgresQuery,
  wipConfig,
}: EditPostgresQueryModalProps<T>) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onCancel();
      }}
      size="3xl"
    >
      <ModalOverlay />
      <ModalContent padding="20px" maxWidth="min-content" minWidth="3xl">
        <ModalHeader>Edit Postgres Query</ModalHeader>
        <Text pl="20px" fontStyle="italic" fontSize="xs" mb="3">
          &quot;triggerDate&quot; variable is configured by default.
        </Text>
        <ModalCloseButton />
        <ModalBody width="100%">
          <Formik
            initialValues={{ ...postgresQuery }}
            onSubmit={(props) => {
              onSave(props);
            }}
          >
            {(formProps: FormikProps<T>) => (
              <Form onSubmit={formProps.handleSubmit}>
                <TextInputFormField label="Name" name="name" />
                <TextInputFormField label="Type" name="type" readOnly />
                <Text mt="3" mb="3">
                  Parameters:
                </Text>
                <PostgresQueryTypeView<T>
                  formProps={formProps}
                  wipConfig={wipConfig}
                />
                <Flex padding-top="5px" justifyContent="center">
                  <Button type="submit" mr="5px">
                    Done
                  </Button>
                  <Button onClick={() => onCancel()}>Cancel</Button>
                </Flex>
              </Form>
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
